/**
 * Guardian Portfolio — seed data, scoring rules, pricing math, and the
 * deterministic CompliMind query matcher.
 *
 * Tunable anchors live at the TOP — change a number, the whole console
 * recomputes. Nothing here is decorative; every value drives a real
 * interactive behaviour in the UI.
 *
 *  * MRPC reminder: legal work product is walled off from the manager
 *  * view; CompliSun Legal, PLC line items never feed manager earnings.
 */

/* ============================================================
 * 1. Module catalog
 * ============================================================ */
export const MODULES = [
  { key: "base",        name: "Base Guardian tenant",         description: "Single source of truth, CompliMind, project/task mgmt, Report Card, gap analysis.", domains: [], locked: true },
  { key: "vendor",      name: "Vendor Management / TPRM",     description: "Third-party oversight per the June 2023 Interagency Guidance.",                       domains: ["Vendor/TPRM"] },
  { key: "screening",   name: "Screening — ComplyCube",       description: "OFAC / PEP / adverse-media screening + red-flag/UAR queue.",                          domains: ["BSA/AML"] },
  { key: "monitoring",  name: "Monitoring & Testing",         description: "Routine independent monitoring and testing of compliance controls.",                  domains: ["BSA/AML","UDAAP"] },
  { key: "onboarding",  name: "Client Onboarding (CDD/EDD)",  description: "Customer due diligence and enhanced due diligence workflows.",                        domains: ["BSA/AML"] },
  { key: "marketing",   name: "Marketing & Advertising Review", description: "UDAAP-grade review of consumer-facing claims.",                                     domains: ["UDAAP"] },
  { key: "training",    name: "Training",                     description: "Assigned, tracked, and attested-to compliance training.",                             domains: ["Training"] },
  { key: "privacy",     name: "Privacy Program",              description: "GLBA/state privacy notices + safeguards.",                                            domains: ["Privacy"] },
  { key: "licensing",   name: "Lending & Licensing",          description: "Multi-state lending license tracking & renewals.",                                    domains: ["Lending & Licensing"] },
];

export const DOMAINS = ["BSA/AML", "UDAAP", "Lending & Licensing", "Vendor/TPRM", "Privacy", "Training"];

/* ============================================================
 * 2. Scoring rules (rules-based Report Card)
 *    Every number here is intentional and tunable.
 * ============================================================ */
const PENALTY = {
  doc_expired:        8,
  doc_in_review_old:  3,   // in_review > 14 days
  task_high_overdue:  10,
  task_med_overdue:   5,
  task_low_overdue:   2,
  screen_flag:        15,
  screen_review:      7,
  vendor_critical_bad: 10, // critical vendor with expired/missing SOC2
  vendor_high_late:   4,   // high vendor overdue re-attestation
};

function clamp(n, lo = 0, hi = 100) { return Math.max(lo, Math.min(hi, n)); }

/** Compute one domain's sub-score for a given company. */
export function domainScore(company, domain) {
  let s = 100;
  for (const d of company.documents || []) {
    if (d.domain !== domain) continue;
    if (d.status === "expired") s -= PENALTY.doc_expired;
    if (d.status === "in_review" && d.in_review_days > 14) s -= PENALTY.doc_in_review_old;
  }
  for (const t of company.tasks || []) {
    if (t.domain !== domain) continue;
    if (t.status !== "open" || !t.overdue) continue;
    if (t.severity === "high") s -= PENALTY.task_high_overdue;
    else if (t.severity === "med") s -= PENALTY.task_med_overdue;
    else s -= PENALTY.task_low_overdue;
  }
  if (domain === "BSA/AML") {
    for (const r of company.screening || []) {
      if (r.resolved) continue;
      if (r.ofac === "flag" || r.pep === "flag" || r.adverseMedia === "flag") s -= PENALTY.screen_flag;
      else if (r.ofac === "review" || r.pep === "review" || r.adverseMedia === "review") s -= PENALTY.screen_review;
    }
  }
  if (domain === "Vendor/TPRM") {
    for (const v of company.vendors || []) {
      if (v.tier === "critical" && (v.soc2 === "expired" || v.soc2 === "missing")) s -= PENALTY.vendor_critical_bad;
      if (v.tier === "high" && v.status === "remediation") s -= PENALTY.vendor_high_late;
    }
  }
  if (domain === "Training") {
    const a = company.training?.assigned || 0;
    const c = company.training?.completed || 0;
    if (a > 0) s = Math.round(100 * c / a);
  }
  return clamp(Math.round(s));
}

/** Active domains for a company = union of every active module's `domains`. */
export function activeDomains(company) {
  const set = new Set();
  for (const m of MODULES) {
    if (!company.activeModules.includes(m.key)) continue;
    m.domains.forEach((d) => set.add(d));
  }
  return Array.from(set);
}

/** Per-domain map, with `null` for domains that are not configured. */
export function domainScoreMap(company) {
  const active = new Set(activeDomains(company));
  const out = {};
  for (const d of DOMAINS) out[d] = active.has(d) ? domainScore(company, d) : null;
  return out;
}

/** Overall score = mean of configured (non-null) domain scores. */
export function overallScore(company) {
  const map = domainScoreMap(company);
  const vals = Object.values(map).filter((v) => v !== null);
  if (vals.length === 0) return 0;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

export function rag(score) {
  if (score >= 80) return "green";
  if (score >= 60) return "amber";
  return "red";
}

export function hasUnresolvedFlag(company) {
  return (company.screening || []).some(
    (r) => !r.resolved && (r.ofac === "flag" || r.pep === "flag" || r.adverseMedia === "flag")
  );
}

/* ============================================================
 * 3. Portfolio roll-up
 * ============================================================ */
export function portfolioRollup(companies) {
  const scores = companies.map(overallScore);
  const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  let greens = 0, ambers = 0, reds = 0;
  for (const s of scores) { const r = rag(s); if (r === "green") greens++; else if (r === "amber") ambers++; else reds++; }
  const heatmap = {};
  for (const d of DOMAINS) {
    const vals = companies.map((c) => domainScoreMap(c)[d]).filter((v) => v !== null);
    heatmap[d] = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  }
  return { avg, greens, ambers, reds, heatmap, total: companies.length };
}

/* ============================================================
 * 4. Pricing math
 * ============================================================ */
export const PRICING = {
  platformPerCoMo:         1000,
  bundlePerCoMo:           3225,   // platform + 20 committed hours
  adHocHourly:             200,
  committedHourly:         110,
  installPerCo:            1500,
  volume: [
    { upTo: 10, perCo: 1000 },
    { upTo: 25, perCo: 850 },
    { upTo: Infinity, perCo: 750 },
  ],
  wholesalePerCoDefault:   800,
  referralPctDefault:      15,
};

export function platformPerCo(N, useVolumeDiscount) {
  if (!useVolumeDiscount) return PRICING.platformPerCoMo;
  for (const tier of PRICING.volume) if (N <= tier.upTo) return tier.perCo;
  return PRICING.platformPerCoMo;
}

/**
 * Compute monthly economics for the calculator.
 *
 * Returns:
 *  - companiesSpend: what the companies pay in aggregate
 *  - csRevenue: CompliSun Inc. revenue (platform + compliance time)
 *  - installRevenueOnce: one-time install
 *  - legalARR: $0 by default — legal is intentionally walled off
 *  - managerEarnings / managerNetCost: depending on managerModel
 *  - relationshipARR: companiesSpend * 12 + installRevenueOnce (annualised)
 */
export function computeEconomics({
  structure,           // "watch_oncall" | "portfolio_retainer" | "per_company_bundle"
  N,
  adHocHours = 0,
  pooledHours = 0,
  bundleCos = 0,
  volumeDiscount = false,
  installEnabled = false,
  installCos = 0,
  managerModel = "passthrough",  // "passthrough" | "wholesale_markup" | "referral_share"
  wholesalePerCo = PRICING.wholesalePerCoDefault,
  retailPerCo,                       // companies pay this when wholesale_markup
  referralPct = PRICING.referralPctDefault,
}) {
  const ppc = platformPerCo(N, volumeDiscount);
  let platformMonthly = 0;
  let hoursMonthly = 0;

  if (structure === "watch_oncall") {
    platformMonthly = N * ppc;
    hoursMonthly = adHocHours * PRICING.adHocHourly;
  } else if (structure === "portfolio_retainer") {
    platformMonthly = N * ppc;
    hoursMonthly = pooledHours * PRICING.committedHourly;
  } else if (structure === "per_company_bundle") {
    const bcap = Math.min(Math.max(0, bundleCos), N);
    platformMonthly = bcap * PRICING.bundlePerCoMo + (N - bcap) * ppc;
    hoursMonthly = adHocHours * PRICING.adHocHourly;
  }

  const csMonthly = platformMonthly + hoursMonthly;
  const csARR = csMonthly * 12;
  const installRevenueOnce = installEnabled ? Math.max(0, Math.min(installCos, N)) * PRICING.installPerCo : 0;

  // Manager economics (only on the platform portion)
  let managerEarningsMonthly = 0;
  let managerNetCostMonthly = 0;
  let companiesSpendMonthly = 0;
  let managerLabel = "Manager net cost (manager funds engagement)";

  if (managerModel === "passthrough") {
    // Companies pay platform; manager pays for expert/retainer.
    companiesSpendMonthly = platformMonthly;
    managerNetCostMonthly = hoursMonthly;
    managerLabel = "Manager net cost (expert / retainer side)";
  } else if (managerModel === "wholesale_markup") {
    // Manager buys platform wholesale and resells at retail.
    const retail = Math.max(0, retailPerCo ?? ppc);
    const wholesale = Math.max(0, Math.min(wholesalePerCo, retail));
    companiesSpendMonthly = N * retail + (structure === "per_company_bundle" ? 0 : 0);
    const platformWholesaleCost = N * wholesale;
    managerEarningsMonthly = companiesSpendMonthly - platformWholesaleCost - hoursMonthly;
    managerLabel = "Manager earnings (markup on platform)";
  } else if (managerModel === "referral_share") {
    // Manager gets a % of platform revenue.
    companiesSpendMonthly = platformMonthly + hoursMonthly;
    managerEarningsMonthly = Math.round(platformMonthly * (referralPct / 100));
    managerLabel = "Manager earnings (referral share, platform only)";
  }

  const legalARR = 0; // Legal is intentionally $0 here — quoted separately by CompliSun Legal, PLC.

  return {
    ppc,
    platformMonthly,
    hoursMonthly,
    csMonthly,
    csARR,
    installRevenueOnce,
    companiesSpendMonthly,
    companiesSpendARR: companiesSpendMonthly * 12,
    managerEarningsMonthly,
    managerNetCostMonthly,
    managerEarningsARR: managerEarningsMonthly * 12,
    managerNetCostARR: managerNetCostMonthly * 12,
    managerLabel,
    legalARR,
    relationshipARR: companiesSpendMonthly * 12 + installRevenueOnce,
  };
}

/* ============================================================
 * 5. Seed data — 8 companies hand-tuned to land near target scores
 * ============================================================ */
let _id = 1;
const nid = () => `id-${_id++}`;

export const SAMPLE_COMPANIES = [
  {
    id: "co-lender",
    name: "Lender Co",
    vertical: "Lending",
    activeModules: ["base", "screening", "monitoring", "marketing", "training", "licensing"],
    documents: [
      { name: "BSA Policy", domain: "BSA/AML", status: "current", expiresOn: "2026-09-01" },
      { name: "UDAAP Marketing Review", domain: "UDAAP", status: "current", expiresOn: "2026-08-01" },
      { name: "State Lending Licenses", domain: "Lending & Licensing", status: "current", expiresOn: "2026-12-31" },
    ],
    tasks: [
      { id: nid(), title: "Annual BSA risk assessment", domain: "BSA/AML", status: "open", overdue: false, severity: "med", dueOn: "2026-05-01" },
    ],
    screening: [
      { id: nid(), subject: "Joaquin Ortiz", ofac: "clear", pep: "clear", adverseMedia: "clear", resolved: true },
    ],
    vendors: [],
    training: { assigned: 100, completed: 92 },
  },
  {
    id: "co-pay",
    name: "Pay Stream",
    vertical: "Payments",
    activeModules: ["base", "vendor", "screening", "marketing", "training", "privacy"],
    documents: [
      { name: "Payments AML Policy", domain: "BSA/AML", status: "current", expiresOn: "2026-09-01" },
      { name: "Privacy Notice", domain: "Privacy", status: "in_review", in_review_days: 9, expiresOn: null },
    ],
    tasks: [
      { id: nid(), title: "Vendor reattestation pass", domain: "Vendor/TPRM", status: "open", overdue: true, severity: "low", dueOn: "2026-01-15" },
    ],
    screening: [
      { id: nid(), subject: "Helene Marquez", ofac: "clear", pep: "review", adverseMedia: "clear", resolved: true },
    ],
    vendors: [
      { name: "CardCloud", tier: "high", soc2: "current", reattestDue: "2026-07-01", status: "monitored" },
      { name: "InboundKYC", tier: "moderate", soc2: "current", reattestDue: "2026-06-01", status: "monitored" },
    ],
    training: { assigned: 80, completed: 70 },
  },
  {
    id: "co-cardworks",
    name: "CardWorks Jr",
    vertical: "BaaS",
    activeModules: ["base", "vendor", "screening", "monitoring", "training"],
    documents: [
      { name: "BSA Policy", domain: "BSA/AML", status: "in_review", in_review_days: 22, expiresOn: null },
      { name: "Sponsor Bank Playbook", domain: "Vendor/TPRM", status: "current", expiresOn: "2026-12-01" },
    ],
    tasks: [
      { id: nid(), title: "UDAAP control test — Q1", domain: "UDAAP", status: "open", overdue: true, severity: "med", dueOn: "2026-01-10" },
      { id: nid(), title: "Update sponsor-bank attestation", domain: "Vendor/TPRM", status: "open", overdue: true, severity: "med", dueOn: "2026-01-20" },
    ],
    screening: [
      { id: nid(), subject: "Devon Lee", ofac: "clear", pep: "clear", adverseMedia: "review", resolved: false },
    ],
    vendors: [
      { name: "ChargeGrid", tier: "critical", soc2: "current", reattestDue: "2026-04-01", status: "monitored" },
      { name: "RailwayKYC", tier: "high", soc2: "current", reattestDue: "2026-03-15", status: "remediation" },
    ],
    training: { assigned: 60, completed: 50 },
  },
  {
    id: "co-uw",
    name: "Underwrite.ai",
    vertical: "Underwriting",
    activeModules: ["base", "monitoring", "marketing", "training"],
    documents: [
      { name: "Model Risk Doc", domain: "UDAAP", status: "expired", expiresOn: "2025-12-01" },
      { name: "Disparate Impact Memo", domain: "UDAAP", status: "in_review", in_review_days: 18, expiresOn: null },
    ],
    tasks: [
      { id: nid(), title: "UDAAP marketing review (Q4)", domain: "UDAAP", status: "open", overdue: true, severity: "high", dueOn: "2025-12-15" },
      { id: nid(), title: "Independent monitoring pass", domain: "BSA/AML", status: "open", overdue: true, severity: "med", dueOn: "2025-12-01" },
    ],
    screening: [],
    vendors: [],
    training: { assigned: 40, completed: 33 },
  },
  {
    id: "co-remit",
    name: "RemitX",
    vertical: "Money Transmission",
    activeModules: ["base", "screening", "monitoring", "onboarding", "training", "licensing"],
    documents: [
      { name: "MSB Registration", domain: "Lending & Licensing", status: "current", expiresOn: "2026-09-01" },
      { name: "State MTL Tracker", domain: "Lending & Licensing", status: "current", expiresOn: "2026-12-01" },
    ],
    tasks: [
      { id: nid(), title: "EDD refresh — Tier 2 customers", domain: "BSA/AML", status: "open", overdue: true, severity: "low", dueOn: "2026-01-12" },
    ],
    screening: [
      { id: nid(), subject: "Anya Volkov", ofac: "review", pep: "clear", adverseMedia: "clear", resolved: false },
    ],
    vendors: [],
    training: { assigned: 50, completed: 47 },
  },
  {
    id: "co-earlywage",
    name: "Earlywage",
    vertical: "EWA",
    activeModules: ["base", "vendor", "screening", "monitoring", "marketing", "training"],
    documents: [
      { name: "Tip Disclosure Policy", domain: "UDAAP", status: "expired", expiresOn: "2025-10-01" },
      { name: "Fee Transparency Memo", domain: "UDAAP", status: "expired", expiresOn: "2025-11-15" },
      { name: "EWA Marketing Guidelines", domain: "UDAAP", status: "in_review", in_review_days: 28, expiresOn: null },
      { name: "BSA Policy", domain: "BSA/AML", status: "expired", expiresOn: "2025-09-15" },
      { name: "AML Risk Assessment", domain: "BSA/AML", status: "in_review", in_review_days: 30, expiresOn: null },
    ],
    tasks: [
      { id: nid(), title: "Address CFPB advisory opinion gap", domain: "UDAAP", status: "open", overdue: true, severity: "high", dueOn: "2025-11-30" },
      { id: nid(), title: "Tip-screen disclosure remediation", domain: "UDAAP", status: "open", overdue: true, severity: "high", dueOn: "2025-12-05" },
      { id: nid(), title: "Marketing review backlog", domain: "UDAAP", status: "open", overdue: true, severity: "med", dueOn: "2026-01-05" },
      { id: nid(), title: "BSA program independence review", domain: "BSA/AML", status: "open", overdue: true, severity: "high", dueOn: "2025-11-15" },
      { id: nid(), title: "SAR backlog clearance", domain: "BSA/AML", status: "open", overdue: true, severity: "high", dueOn: "2025-12-20" },
      { id: nid(), title: "PayrollLink vendor remediation plan", domain: "Vendor/TPRM", status: "open", overdue: true, severity: "high", dueOn: "2025-11-05" },
    ],
    screening: [
      { id: nid(), subject: "Marcus Holloway", ofac: "clear", pep: "clear", adverseMedia: "flag", resolved: false },
    ],
    vendors: [
      { name: "PayrollLink", tier: "critical", soc2: "expired", reattestDue: "2025-11-01", status: "remediation" },
      { name: "OnDemandIdent", tier: "critical", soc2: "missing", reattestDue: "2025-10-01", status: "remediation" },
    ],
    training: { assigned: 30, completed: 8 },
  },
  {
    id: "co-lendmatch",
    name: "Lendmatch",
    vertical: "Marketplace",
    activeModules: ["base", "vendor", "monitoring", "marketing", "training"],
    documents: [
      { name: "Lead-gen Disclosures", domain: "UDAAP", status: "in_review", in_review_days: 19, expiresOn: null },
    ],
    tasks: [
      { id: nid(), title: "Partner monitoring refresh", domain: "Vendor/TPRM", status: "open", overdue: true, severity: "med", dueOn: "2026-01-15" },
      { id: nid(), title: "Advertising claims audit", domain: "UDAAP", status: "open", overdue: true, severity: "low", dueOn: "2026-01-22" },
    ],
    screening: [],
    vendors: [
      { name: "RailLeads", tier: "high", soc2: "current", reattestDue: "2026-02-01", status: "remediation" },
    ],
    training: { assigned: 25, completed: 22 },
  },
  {
    id: "co-custody",
    name: "Custody One",
    vertical: "Custody",
    activeModules: ["base", "vendor", "screening", "monitoring", "training", "privacy"],
    documents: [
      { name: "Custody Policy", domain: "BSA/AML", status: "current", expiresOn: "2026-10-01" },
      { name: "Privacy Notice", domain: "Privacy", status: "current", expiresOn: "2026-11-01" },
    ],
    tasks: [],
    screening: [
      { id: nid(), subject: "Sven Hofstadter", ofac: "clear", pep: "clear", adverseMedia: "clear", resolved: true },
    ],
    vendors: [
      { name: "ColdStore", tier: "critical", soc2: "current", reattestDue: "2026-08-01", status: "monitored" },
    ],
    training: { assigned: 70, completed: 65 },
  },
];

/* ============================================================
 * 6. CompliMind — deterministic query matcher
 *    Rule-matches a handful of intents and computes the answer
 *    from the live state. No LLM required for the demo.
 * ============================================================ */
function fmt$(n) { return "$" + (n || 0).toLocaleString(); }

export function answerCompliMind(query, companies) {
  const q = (query || "").toLowerCase().trim();
  const sources = new Set();
  let text = "";

  const addCo = (id) => sources.add(id);

  // Intent: open OFAC / screening flags
  if (/(open|unresolved).*(ofac|screen|flag)/.test(q) || /which.*flag/.test(q)) {
    const hits = [];
    for (const c of companies) {
      for (const r of c.screening || []) {
        if (r.resolved) continue;
        const tag = r.ofac === "flag" || r.pep === "flag" || r.adverseMedia === "flag" ? "flag"
                  : r.ofac === "review" || r.pep === "review" || r.adverseMedia === "review" ? "review" : null;
        if (!tag) continue;
        hits.push(`• ${c.name} → ${r.subject} (${tag})`);
        addCo(c.id);
      }
    }
    text = hits.length
      ? `${hits.length} unresolved screening item${hits.length === 1 ? "" : "s"} across the portfolio:\n${hits.join("\n")}`
      : "Nothing open — every screening hit across the portfolio is resolved.";
  }

  // Intent: companies below threshold on a domain
  else if (/below\s*(\d+).*(udaap|bsa|aml|lending|vendor|privacy|training)/.test(q) || /under\s*(\d+).*(udaap|bsa|aml|lending|vendor|privacy|training)/.test(q)) {
    const m = q.match(/\b(\d{2,3})\b/);
    const threshold = m ? parseInt(m[1], 10) : 70;
    const domainLookup = {
      udaap: "UDAAP", "bsa": "BSA/AML", aml: "BSA/AML", "bsa/aml": "BSA/AML",
      lending: "Lending & Licensing", licensing: "Lending & Licensing",
      vendor: "Vendor/TPRM", tprm: "Vendor/TPRM", privacy: "Privacy", training: "Training",
    };
    const domainKey = Object.keys(domainLookup).find((k) => q.includes(k));
    const domain = domainLookup[domainKey];
    const hits = companies
      .map((c) => ({ c, s: domainScoreMap(c)[domain] }))
      .filter((x) => x.s !== null && x.s < threshold);
    hits.forEach((h) => addCo(h.c.id));
    text = hits.length
      ? `${hits.length} compan${hits.length === 1 ? "y" : "ies"} below ${threshold} on ${domain}:\n${hits.map((h) => `• ${h.c.name} — ${h.s}/100`).join("\n")}`
      : `Everyone is at or above ${threshold} on ${domain}.`;
  }

  // Intent: SOC 2 overdue vendors
  else if (/(soc\s*2|vendor).*(overdue|expired|missing|reattest|re-attest)/.test(q) || /vendors? overdue/.test(q)) {
    const hits = [];
    for (const c of companies) {
      for (const v of c.vendors || []) {
        if (v.soc2 === "expired" || v.soc2 === "missing" || v.status === "remediation") {
          hits.push(`• ${c.name} → ${v.name} (${v.tier} tier · SOC2 ${v.soc2} · ${v.status})`);
          addCo(c.id);
        }
      }
    }
    text = hits.length
      ? `${hits.length} vendor issue${hits.length === 1 ? "" : "s"} across the portfolio:\n${hits.join("\n")}`
      : "No SOC 2 issues across the portfolio. All critical vendors are current.";
  }

  // Intent: summarize red items for a specific company
  else if (/(summari[sz]e|summary).*\bfor\b/.test(q) || /red items? for/.test(q)) {
    const co = companies.find((c) => q.includes(c.name.toLowerCase()));
    if (co) {
      addCo(co.id);
      const tasks = (co.tasks || []).filter((t) => t.status === "open" && t.overdue && t.severity === "high");
      const docs = (co.documents || []).filter((d) => d.status === "expired");
      const flags = (co.screening || []).filter((r) => !r.resolved && (r.ofac === "flag" || r.pep === "flag" || r.adverseMedia === "flag"));
      const lines = [];
      if (tasks.length) lines.push("Open high-severity tasks:\n" + tasks.map((t) => `  • ${t.title} (due ${t.dueOn})`).join("\n"));
      if (docs.length) lines.push("Expired documents:\n" + docs.map((d) => `  • ${d.name} (expired ${d.expiresOn})`).join("\n"));
      if (flags.length) lines.push("Unresolved screening flags:\n" + flags.map((r) => `  • ${r.subject}`).join("\n"));
      text = lines.length
        ? `Red items for ${co.name} (score ${overallScore(co)}/100):\n\n` + lines.join("\n\n")
        : `${co.name} is at ${overallScore(co)}/100 with no red items right now.`;
    } else {
      text = "Tell me which company — I see: " + companies.map((c) => c.name).join(", ") + ".";
    }
  }

  // Intent: portfolio average + RAG counts
  else if (/(portfolio|overall).*(average|avg|mean)|how many reds|how many greens/.test(q)) {
    const r = portfolioRollup(companies);
    companies.forEach((c) => addCo(c.id));
    text = `Portfolio average: ${r.avg}/100 across ${r.total} companies.\n• Green: ${r.greens}\n• Amber: ${r.ambers}\n• Red: ${r.reds}`;
  }

  // Intent: biggest risk
  else if (/(biggest|highest|worst).*risk|lowest.*(score|company)/.test(q)) {
    const sorted = [...companies].sort((a, b) => overallScore(a) - overallScore(b));
    const worst = sorted[0];
    if (worst) {
      addCo(worst.id);
      const dm = domainScoreMap(worst);
      const weak = Object.entries(dm).filter(([, v]) => v !== null).sort((a, b) => a[1] - b[1]).slice(0, 2);
      text = `Biggest risk right now: ${worst.name} — ${overallScore(worst)}/100 (${worst.vertical}).\nWeakest domains:\n${weak.map(([d, s]) => `  • ${d}: ${s}/100`).join("\n")}\n${hasUnresolvedFlag(worst) ? "⚠ Unresolved screening flag." : ""}`.trim();
    }
  }

  if (!text) {
    text = "I can answer portfolio-wide questions like:\n• Which companies have an open OFAC or screening flag?\n• Show every company below 70 on UDAAP.\n• Which vendors are overdue for SOC 2 across the portfolio?\n• Summarize the red items for Earlywage.\n• What's my portfolio average and how many reds?\n• Which company is my biggest risk right now?";
  }

  return {
    text,
    sources: Array.from(sources),
    privilegeNote: "Compliance posture only. Privileged legal content stays with the company-client.",
  };
}

export const SUGGESTED_PROMPTS = [
  "Which companies have an open OFAC or screening flag?",
  "Show every company below 70 on UDAAP.",
  "Which vendors are overdue for SOC 2 across the portfolio?",
  "Summarize the red items for Earlywage.",
  "What's my portfolio average and how many reds?",
  "Which company is my biggest risk right now?",
];

/* Helpful formatters reused across screens */
export const fmt = {
  money: (n) => "$" + Math.round(n || 0).toLocaleString(),
  pct: (n) => (n === null || n === undefined) ? "—" : `${n}%`,
};
